import * as React from "react"
import { toast as sonnerToast } from "sonner"

/**
 * Sonner-backed compatibility shim for the former shadcn/Radix toast store.
 *
 * The old module-store pipeline (components/ui/toast.tsx + ui/toaster.tsx) was
 * never mounted, so every `toast()` call routed here used to render nothing.
 * This module now delegates to sonner (the `<Toaster />` mounted in App.tsx)
 * while keeping the historical call signature — `toast({ title, description,
 * variant, ... })` — so the ~35 importing files need no changes.
 *
 * Mapping:
 * - `variant: "destructive"` → `sonnerToast.error(title, { description })`
 * - default / no variant     → `sonnerToast(title, { description })`
 * - `action: { label, onClick }` → sonner's `action` option
 *
 * All other sonner methods (`.success`, `.error`, `.dismiss`, ...) are
 * forwarded untouched, and the returned value is sonner's toast id.
 */

export type ToastVariant = "default" | "destructive"

export interface ToastAction {
  label: React.ReactNode
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
}

export interface ToastOptions {
  title?: React.ReactNode
  description?: React.ReactNode
  variant?: ToastVariant
  action?: ToastAction
  duration?: number
  id?: string | number
}

function showToast(options: ToastOptions): string | number {
  const { title, description, variant, action, duration, id } = options

  const sonnerOptions = {
    description,
    duration,
    id,
    action:
      action === undefined
        ? undefined
        : { label: action.label, onClick: action.onClick },
  }

  if (variant === "destructive") {
    return sonnerToast.error(title, sonnerOptions)
  }

  return sonnerToast(title, sonnerOptions)
}

/**
 * Callable with the legacy object signature, with every sonner method
 * (`toast.success`, `toast.dismiss`, ...) attached for direct sonner parity.
 */
const toast = Object.assign(showToast, sonnerToast)

function useToast() {
  return {
    toast,
    dismiss: sonnerToast.dismiss,
  }
}

export { useToast, toast }
